import React from 'react';
import L from 'leaflet';
import { Map as LeafletMap, TileLayer, Marker, Circle, Polygon, CircleMarker } from 'react-leaflet';
import pin256 from '../../assets/images/pin256.png';
import '../../styles/modals/deliveryzonesmap.css';

const COLOURS = ['#fec802', '#2f80ed', '#27ae60', '#eb5757', '#9b51e0', '#f2994a'];
const colourFor = (index) => COLOURS[index % COLOURS.length];

class DeliveryZonesMapModal extends React.Component {
  constructor(props) {
    super(props);

    /** drawingIndex is the zone being drawn or edited; points are its corners so far. */
    this.state = { drawingIndex: null, points: [] };
  }

  componentDidMount() {
    setTimeout(() => this.fitMap(), 50);
  }

  fitMap() {
    if (!this.map) {
      return;
    }

    const { lat, lng, zones } = this.props;
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    this.map.leafletElement.invalidateSize();

    const drawn = (zones || []).filter((zone) => Array.isArray(zone.polygon) && zone.polygon.length > 2);
    const maxMiles = (zones || []).reduce((max, zone) => Math.max(max, parseFloat(zone.max_distance_miles) || 0), 0);

    if (drawn.length > 0) {
      const points = drawn.reduce((all, zone) => all.concat(zone.polygon.map((p) => [p.lat, p.lng])), [[latitude, longitude]]);
      this.map.leafletElement.fitBounds(L.latLngBounds(points), { padding: [45, 45] });
    } else if (maxMiles > 0) {
      const bounds = L.circle([latitude, longitude], { radius: maxMiles * 1609.344 }).getBounds();
      this.map.leafletElement.fitBounds(bounds, { padding: [45, 45] });
    } else {
      this.map.leafletElement.setView([latitude, longitude], 13);
    }
  }

  startDrawing(index) {
    const zone = this.props.zones[index];
    const existing = Array.isArray(zone.polygon) ? zone.polygon : [];
    this.setState({ drawingIndex: index, points: existing.map((p) => ({ lat: p.lat, lng: p.lng })) });
  }

  onMapClick = (event) => {
    if (this.state.drawingIndex === null) {
      return;
    }
    const { lat, lng } = event.latlng;
    this.setState({ points: [...this.state.points, { lat, lng }] });
  };

  /** Clicking the first point closes the shape, as on GloriaFood. */
  finish = () => {
    const { drawingIndex, points } = this.state;
    if (points.length > 2 && this.props.onZoneDrawn) {
      this.props.onZoneDrawn(drawingIndex, points);
    }
    this.setState({ drawingIndex: null, points: [] });
  };

  cancel = () => {
    this.setState({ drawingIndex: null, points: [] });
  };

  undo = () => {
    this.setState({ points: this.state.points.slice(0, -1) });
  };

  movePoint(pointIndex, latlng) {
    const points = this.state.points.map((p, i) => (i === pointIndex ? { lat: latlng.lat, lng: latlng.lng } : p));
    this.setState({ points });
  }

  render() {
    const { venueName, lat, lng, zones, onClose, onDeleteZone, onClearPolygon, onToggleZone } = this.props;
    const { drawingIndex, points } = this.state;

    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    const hasLocation = Number.isFinite(latitude) && Number.isFinite(longitude);
    const drawing = drawingIndex !== null;

    const parsedZones = (zones || []).map((zone, index) => ({
      index,
      zone: index + 1,
      name: zone.name || `Zone ${index + 1}`,
      enabled: zone.enabled !== false,
      polygon: Array.isArray(zone.polygon) ? zone.polygon : [],
      miles: parseFloat(zone.max_distance_miles) || 0,
      colour: colourFor(index),
    }));

    return (
      <div className="delivery-zones-modal-overlay">
        <div className="delivery-zones-modal-card">
          <div className="delivery-zones-modal-header">
            <span>{venueName || 'Delivery zones'}</span>
            <button type="button" className="delivery-zones-modal-x" onClick={onClose}>
              &times;
            </button>
          </div>

          {!hasLocation ? (
            <div className="delivery-zones-map-empty">Pin the venue location first to view delivery zones.</div>
          ) : (
            <>
              {drawing && (
                <div className="delivery-zones-draw-bar">
                  <span>
                    Click the map to add points. Click the first point to finish. {points.length} point{points.length === 1 ? '' : 's'}.
                  </span>
                  <span>
                    <button type="button" className="delivery-zones-draw-btn" disabled={points.length < 1} onClick={this.undo}>
                      Undo point
                    </button>
                    <button type="button" className="delivery-zones-draw-btn" disabled={points.length < 3} onClick={this.finish}>
                      Finish
                    </button>
                    <button type="button" className="delivery-zones-draw-btn" onClick={this.cancel}>
                      Cancel
                    </button>
                  </span>
                </div>
              )}

              <LeafletMap
                className="delivery-zones-map"
                center={[latitude, longitude]}
                zoom={13}
                onClick={this.onMapClick}
                ref={(ref) => (this.map = ref)}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />

                {parsedZones.map((zone) =>
                  zone.index === drawingIndex ? null : zone.polygon.length > 2 ? (
                    <Polygon
                      key={`p_${zone.index}`}
                      positions={zone.polygon.map((p) => [p.lat, p.lng])}
                      color={zone.colour}
                      fillOpacity={zone.enabled ? 0.18 : 0.05}
                      dashArray={zone.enabled ? null : '6'}
                    />
                  ) : zone.miles > 0 ? (
                    <Circle
                      key={`c_${zone.index}`}
                      center={[latitude, longitude]}
                      radius={zone.miles * 1609.344}
                      color={zone.colour}
                      fillOpacity={zone.enabled ? 0.12 : 0.04}
                      dashArray={zone.enabled ? null : '6'}
                    />
                  ) : null,
                )}

                {drawing && points.length > 1 && (
                  <Polygon positions={points.map((p) => [p.lat, p.lng])} color={colourFor(drawingIndex)} fillOpacity={0.15} />
                )}

                {drawing &&
                  points.map((point, i) => (
                    <CircleMarker
                      key={`dp_${i}`}
                      center={[point.lat, point.lng]}
                      radius={i === 0 ? 8 : 5}
                      color={colourFor(drawingIndex)}
                      fillColor="#ffffff"
                      fillOpacity={1}
                      onClick={(e) => {
                        if (i === 0 && points.length > 2) {
                          e.originalEvent.stopPropagation();
                          this.finish();
                        }
                      }}
                    />
                  ))}

                <Marker
                  position={[latitude, longitude]}
                  icon={L.icon({ iconUrl: pin256, iconSize: [32, 32], iconAnchor: [16, 32] })}
                />
              </LeafletMap>
            </>
          )}

          <div className="delivery-zones-map-legend">
            {parsedZones.length > 0 ? (
              parsedZones.map((zone) => (
                <div className="delivery-zones-map-legend-row" key={zone.index} style={{ opacity: zone.enabled ? 1 : 0.5 }}>
                  <strong style={{ color: zone.colour }}>{zone.name}</strong>
                  <span>{zone.polygon.length > 2 ? 'Drawn' : zone.miles ? `${zone.miles} ${zone.miles === 1 ? 'mile' : 'miles'}` : 'No area'}</span>

                  {hasLocation && !drawing && (
                    <button type="button" className="delivery-zones-map-delete" title="Draw this zone" onClick={() => this.startDrawing(zone.index)}>
                      {zone.polygon.length > 2 ? 'Redraw' : 'Draw'}
                    </button>
                  )}

                  {zone.polygon.length > 2 && !drawing && onClearPolygon && (
                    <button type="button" className="delivery-zones-map-delete" title="Use a radius instead" onClick={() => onClearPolygon(zone.index)}>
                      Use radius
                    </button>
                  )}

                  {onToggleZone && (
                    <button
                      type="button"
                      className="delivery-zones-map-delete"
                      title={zone.enabled ? 'Stop delivering here' : 'Deliver here again'}
                      onClick={() => onToggleZone(zone.index, !zone.enabled)}
                    >
                      {zone.enabled ? 'Hide' : 'Show'}
                    </button>
                  )}

                  <button type="button" className="delivery-zones-map-delete" title="Delete zone" onClick={() => onDeleteZone(zone.index)}>
                    &times;
                  </button>
                </div>
              ))
            ) : (
              <div>No delivery zones configured yet.</div>
            )}
          </div>

          <button type="button" className="btn btn-warning delivery-zones-map-close" onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    );
  }
}

export default DeliveryZonesMapModal;
